import { useCallback } from "react";

import { Button } from "../button/Button";
import { Icon } from "../icon/Icon";

import "./OrderingQuestion.css";

import type { QuizQuestionChangeEvent, QuizQuestionOption } from "../../app";

export interface OrderingQuestionProps {
  answer: string[];
  options: QuizQuestionOption[];
  isValid?: boolean;
  showValidity?: boolean;
  value?: string[];
  onChange?: (event: QuizQuestionChangeEvent) => void;
}

export function getIntent({
  answer,
  option,
  value,
  showValidity,
}: {
  answer: string[];
  option: QuizQuestionOption;
  value: string[];
  showValidity?: boolean;
}) {
  if (!value || !showValidity) return;

  if (value.includes(option.value)) {
    return answer.indexOf(option.value) === value.indexOf(option.value)
      ? "success"
      : "error";
  }
}

export function OrderingQuestion({
  answer,
  isValid,
  options,
  showValidity,
  value = [],
  onChange,
}: OrderingQuestionProps) {
  const defaultValue = options.map((i) => i.value);
  const items = value.length ? value : defaultValue;

  const moveItem = useCallback(
    (itemValue: string, from: number, to: number) => {
      const newValue = [...items];
      newValue.splice(from, 1);
      newValue.splice(to, 0, itemValue);

      onChange?.({ value: newValue });
    },
    [onChange, items],
  );

  const renderItems = useCallback(() => {
    return (
      <ol className="OrderingQuestion__list">
        {items.map((v, i) => {
          const option = options.find((option) => option.value === v)!;

          if (!option) return null;

          const intent = getIntent({
            answer,
            option,
            value,
            showValidity,
          });

          function moveUp() {
            moveItem(option.value, i, Math.max(0, i - 1));
          }

          function moveDown() {
            moveItem(option.value, i, Math.min(items.length - 1, i + 1));
          }

          return (
            <li key={option.value} className="OrderingQuestion__list-item">
              {intent && (
                <Icon
                  icon={intent === "success" ? "circle-check" : "circle-xmark"}
                  variant="solid"
                  color={`var(--color-${intent})`}
                />
              )}
              <div className="OrderingQuestion__item-number">{i + 1}</div>
              <div className="OrderingQuestion__item-text">{option.text}</div>
              <div className="OrderingQuestion__item-controls">
                <Button
                  appearance="minimal"
                  icon="arrow-up"
                  size="sm"
                  disabled={isValid}
                  onClick={moveUp}
                />

                <Button
                  appearance="minimal"
                  icon="arrow-down"
                  size="sm"
                  disabled={isValid}
                  onClick={moveDown}
                />
              </div>
            </li>
          );
        })}
      </ol>
    );
  }, [answer, isValid, items, moveItem, options, showValidity, value]);

  return <div className="OrderingQuestion">{renderItems()}</div>;
}
